import { objects } from "../utils/objects.js";
import { time } from "./time.js";
import { createSingletonLogger } from "./singleton-logger.js";
import { trace } from "./trace.js";

class Event {
  constructor(eventName, eventTime, data) {
    if (!eventName) {
      //does this make sense?
      throw new Error("Missing event name");
    }
    this.name = eventName || "";
    this.data = data || {};
    this.time = eventTime || time.current;
  }

  clone() {
    const clonedData = objects.clone(this.data);
    return new Event(this.name, this.time, clonedData);
  }
}

class Events {
  // Component identity for logging
  static componentName = "events";
  
  constructor() {
    this.namespacesSeparator = ":";

    this._actions = new Map();
    this._eventsQueue = [];
    this._currentEvent = null;
    this._lastEvent = null;

    this._isProcessing = false;
    this._isLogging = false;
    
    // Initialize the singleton logger
    this.#logger = createSingletonLogger(this);

    this.processEventsQueue();
  }
  
  // Private logger instance
  #logger;

  get currentEvent() {
    return this._currentEvent;
  }

  get lastEvent() {
    return this._lastEvent;
  }

  addAction(eventName, eventAction) {
    if (Array.isArray(eventName)) {
      // TODO what is this doing? testing if eventName is array?
      for (const name of eventName) {
        this.addAction(name, eventAction);
      }
      return;
    }

    const eventActions = this._actions.get(eventName) || [];
    for (const savedEventAction of eventActions) {
      if (savedEventAction === eventAction) {
        return eventAction;
      }
    }
    eventActions.push(eventAction);
    this._actions.set(eventName, eventActions);

    return eventAction;
  }

  removeAction(eventName, action) {
    const eventActions = this._actions.get(eventName);
    if (action) {
      const removingActionIndex = eventActions.indexOf(action);
      eventActions.splice(removingActionIndex, 1);
    } else {
      this._actions.delete(eventName);
    }
  }

  addEventToQueue(eventName, eventTime, data) {
    const event = new Event(eventName, eventTime, data);
    this._eventsQueue.push(event);
    if (!this._isProcessing) {
      this.processEventsQueue();
    }
  }

  getActions(eventName) {
    const eventsNames = [];

    // Build prefixes from general to specific
    // For "a:click", this generates: ["a"]
    // For "a:b:c", this generates: ["a", "a:b"]
    // For "a", this generates: []
    const segments = eventName.split(this.namespacesSeparator);
    for (let i = 1; i < segments.length; i++) {
      const prefix = segments.slice(0, i).join(this.namespacesSeparator);
      eventsNames.push(prefix);
    }

    // Add exact match last (most specific)
    eventsNames.push(eventName);

    // Collect actions from general to specific
    return eventsNames.flatMap((name) => this._actions.get(name) || []);
  }

  getEventNamesForUid(entityUid) {
    const string = `${entityUid}:`;
    return [...this._actions]
      .map(([eventName]) => eventName)
      .filter((eventName) => eventName.startsWith(string))
      .map((entityEventName) => entityEventName.replace(string, ""));
  }

  processEventsQueue() {
    // now namespaced events are executed with base first and then its different parts
    // e.g. some_event is ecexuted before some_event:success, even if the second one is before in the config
    if (this._isProcessing) {
      return;
    }

    this._isProcessing = true;
    while (this._eventsQueue.length > 0) {
      this._currentEvent = this._eventsQueue.shift();
      const eventActions = this.getActions(this._currentEvent.name);
      
      // Use the new logging system instead of the old logEvent method
      // Only log if the event isn't related to remote logging (to prevent loops)
      if (!this._currentEvent.name.includes("remote_log")) {
        this.#logger.log(`[${this._currentEvent.name}] with ${eventActions.length} actions`);
      }
      
      // Keep the old method call as a no-op to maintain compatibility
      //this.logEvent(eventActions);
      
      trace.record("events", this._currentEvent.name, "dispatch", { actions: eventActions.length, data: this._currentEvent.data });

      for (const eventAction of eventActions) {
        eventAction(this._currentEvent);
      }
    }

    this._lastEvent = this._currentEvent;
    this._currentEvent = null;
    this._isProcessing = false;
  }

  activateLogs() {
    this._isLogging = true;
  }
}

const events = new Events();
export { Event, Events, events };